/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client/react/hooks';
import * as React from 'react';
import { isFilterMenuOpen } from '../../../apollo/reactiveVariables/MobileApprovalsFilterVariables';
import { useSetFilterDataMutation } from '../../../data/client/mutations/SetFilterData.generated';
import { useSetListDataMutation } from '../../../data/client/mutations/SetListData.generated';
import { useFilterDataQuery } from '../../../data/client/queries/FilterData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { ListData } from '../../../data/types.generated';
import { MobileAdditionalFiltersComponent } from '../../../mobile/components/MobileFilter/MobileAdditionalFilters.component';
import { MobileApprovalsAppFilterComponent } from '../../../mobile/components/MobileFilter/MobileAppovalsAppFilter.component';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ApprovalsAppFilterComponent, ApprovalsAppFilterComponentProps } from './ApprovalsAppFilter.component';
import { LegacyApprovalsAppFilterComponent } from './LegacyApprovalsAppFilter.component';

export const ApprovalsAppFilterContainer: React.FC = (): JSX.Element => {
    const [setListDataMutation] = useSetListDataMutation();
    const [setFilterDataMutation] = useSetFilterDataMutation();
    const {
        data: { listData },
    } = useListDataQuery();
    const {
        data: { filterData },
    } = useFilterDataQuery();

    const isFilterVisible = filterData?.isOpen;
    const isStatusFilterVisible = useReactiveVar<boolean>(isFilterMenuOpen);

    const setListData = React.useCallback(
        (listData: Partial<ListData>): void => {
            setListDataMutation({
                variables: listData,
            });
        },
        [setListDataMutation]
    );

    const onDismiss = React.useCallback((): void => {
        setFilterDataMutation({
            variables: {
                isOpen: false,
            },
        });
    }, [setFilterDataMutation]);

    const componentProps: ApprovalsAppFilterComponentProps = {
        isFilterVisible,
        listData,
        onDismiss,
        setListData,
    };

    const mobileComponentProps: ApprovalsAppFilterComponentProps = {
        isStatusFilterVisible,
        listData,
        onDismiss,
        setListData,
    };

    return getAppHost().isMobile() ? (
        <>
            <MobileApprovalsAppFilterComponent {...mobileComponentProps} />
            <MobileAdditionalFiltersComponent {...mobileComponentProps} />
        </>
    ) : getAppHost().getEcsConfig().MicrosoftTeamApprovals.AdditionalFilters ? (
        <ApprovalsAppFilterComponent {...componentProps} />
    ) : (
        <LegacyApprovalsAppFilterComponent {...componentProps} />
    );
};
